import React from 'react';
import { LuSparkles, LuChevronRight, LuUser } from 'react-icons/lu';
import useEntitlement from '../../hooks/useEntitlement';
import BoneButton from '../oracle/BoneButton';
import AurumButton from '../oracle/AurumButton';
import formatMembershipDate from '../../lib/membershipDate';
import { COPY } from '../../contracts/copyMap';
import { ROUTES } from '../../contracts/routes';
import { ENTITLEMENT_STATUS, trialDaysLeft as trialDaysLeftDoc } from '../../contracts/billingApi';

// The membership block on /account. Replaces a status chip that was a <div>
// styled to look tappable and did nothing, beside no route to /membership at
// all.
//
// ── ONE RESOLVER, SEVEN STATES ──────────────────────────────────────────────
//
// Every state produces the same four slots, so the card never restructures
// itself between renders — only its words change.
//
//   ┌──────────────────────────────────────────┐
//   │ ✦ EYEBROW                    • STANDING  │  tier   /  is it in good order
//   │                                          │
//   │ Headline in serif                        │  the one fact that matters
//   │ detail line, quieter        (optional)    │  consequence or offer
//   │ ┌──────────────────────────────────────┐ │
//   │ │        CTA                        ›  │ │  always a route to /membership
//   │ └──────────────────────────────────────┘ │
//   └──────────────────────────────────────────┘
//
// The paying states leave `detail` empty on purpose: price and renewal date
// are billing detail, and billing detail lives on /membership now. A card that
// restates the table is a worse version of the table.
//
//   error       → UNAVAILABLE, still links out.  See the note below.
//   trialing    → "5 days left in trial"       · (no detail — see below)
//   trial+cancel→ "Access ends <date>"         · your data stays
//   active      → "Membership active"          · (no detail — see below)
//   active+cxl  → "Access ends <date>"         · your data stays
//   past_due    → "Payment unresolved"         · UPDATE PAYMENT, not MANAGE
//   canceled    → "Membership ended"           · your data stays     [upsell]
//   none/other  → "Free account"               · what membership opens [upsell]
//
// [upsell] swaps the neutral CTA for the Aurum one and says UPGRADE NOW. Only
// the two free-tier states get it. past_due does not: DESIGN.md §2.1 allow-
// lists the accent, and selling an upgrade to someone whose card just failed
// misreads the situation — they already bought.
//
// ── WHY THE ERROR STATE EXISTS ──────────────────────────────────────────────
//
// useEntitlement leaves `entitlement` null on a snapshot failure, which is
// indistinguishable from the free tier by shape alone. Reading only the doc
// therefore tells a paying member "FREE ACCOUNT" during an outage — the exact
// lie the loading gate below was written to prevent, arriving through a
// different door. So `error` is read separately and wins over the doc.
//
// It still renders the CTA. Whatever went wrong with the listener, the route
// to the page where they can fix or cancel their billing must not close.

const CTA_TESTID = 'membership-card-cta';

// Trial headline. Day zero is not "0 days left" — that reads as already gone.
function trialHeadline(daysLeft) {
  if (daysLeft == null) return COPY.MEMBERSHIP_CARD_TRIAL_HEADLINE;
  if (daysLeft === 0) return COPY.MEMBERSHIP_CARD_TRIAL_LAST_DAY;
  const suffix = daysLeft === 1
    ? COPY.MEMBERSHIP_CARD_TRIAL_SUFFIX_ONE
    : COPY.MEMBERSHIP_CARD_TRIAL_SUFFIX_MANY;
  return `${daysLeft} ${suffix}`;
}

/**
 * Resolves entitlement state into the card's four slots.
 * Exported for tests — the state table is the whole component.
 */
export function resolveMembershipCard(entitlement, error) {
  if (error) {
    return {
      eyebrow: COPY.MEMBERSHIP_CARD_TIER_MEMBER,
      standing: null,
      headline: COPY.MEMBERSHIP_CARD_UNAVAILABLE_HEADLINE,
      detail: COPY.MEMBERSHIP_CARD_UNAVAILABLE_DETAIL,
      cta: COPY.MEMBERSHIP_MANAGE_CTA,
      pro: false,
    };
  }

  const status = entitlement?.status || ENTITLEMENT_STATUS.NONE;
  const cancelling = Boolean(entitlement?.cancel_at_period_end);
  const periodEnd = formatMembershipDate(entitlement?.current_period_end);
  const trialEnd = formatMembershipDate(entitlement?.trial_end);

  switch (status) {
    case ENTITLEMENT_STATUS.TRIALING: {
      // A cancelled trial ends on trial_end, not current_period_end — those
      // differ, and quoting the wrong one tells someone their access lasts a
      // month longer than it does.
      if (cancelling) {
        return {
          eyebrow: COPY.MEMBERSHIP_STATUS_TRIAL,
          standing: COPY.MEMBERSHIP_CARD_STANDING_ENDING,
          headline: trialEnd
            ? `${COPY.MEMBERSHIP_ENDS_PREFIX} ${trialEnd}`
            : COPY.MEMBERSHIP_CARD_TRIAL_HEADLINE,
          detail: COPY.CANCEL_MODAL_KEEPS_DATA,
          cta: COPY.MEMBERSHIP_MANAGE_CTA,
          pro: true,
        };
      }
      return {
        eyebrow: COPY.MEMBERSHIP_STATUS_TRIAL,
        standing: COPY.MEMBERSHIP_CARD_STANDING_ACTIVE,
        headline: trialHeadline(trialDaysLeftDoc(entitlement)),
        detail: null,
        cta: COPY.MEMBERSHIP_MANAGE_CTA,
        pro: true,
      };
    }

    case ENTITLEMENT_STATUS.ACTIVE:
      return {
        eyebrow: COPY.MEMBERSHIP_CARD_TIER_MEMBER,
        standing: cancelling
          ? COPY.MEMBERSHIP_CARD_STANDING_ENDING
          : COPY.MEMBERSHIP_CARD_STANDING_ACTIVE,
        headline: cancelling && periodEnd
          ? `${COPY.MEMBERSHIP_ENDS_PREFIX} ${periodEnd}`
          : COPY.MEMBERSHIP_CARD_ACTIVE_HEADLINE,
        detail: cancelling ? COPY.CANCEL_MODAL_KEEPS_DATA : null,
        cta: COPY.MEMBERSHIP_MANAGE_CTA,
        pro: true,
      };

    case ENTITLEMENT_STATUS.PAST_DUE:
      // Not gold, and the CTA is the useful action rather than the generic
      // one: what this person needs is the card form, not a menu.
      return {
        eyebrow: COPY.MEMBERSHIP_CARD_TIER_MEMBER,
        standing: COPY.MEMBERSHIP_CARD_STANDING_UNRESOLVED,
        headline: COPY.MEMBERSHIP_CARD_PAST_DUE_HEADLINE,
        detail: COPY.MEMBERSHIP_CARD_PAST_DUE_DETAIL,
        cta: COPY.MEMBERSHIP_UPDATE_PAYMENT_CTA,
        pro: false,
      };

    // The two free-tier states. `upsell` promotes the CTA from the neutral
    // BoneButton to the Aurum treatment MembershipCTA and ProOfferPanel
    // already use — this is the only place on /account with something to
    // sell, and a grey button asking someone to pay is asking quietly.
    //
    // past_due deliberately does NOT get it: that person is already paying,
    // and dressing "fix your card" as an upgrade misreads the situation.
    case ENTITLEMENT_STATUS.CANCELED:
      return {
        eyebrow: COPY.MEMBERSHIP_STATUS_FREE,
        standing: null,
        headline: COPY.MEMBERSHIP_CARD_ENDED_HEADLINE,
        detail: COPY.CANCEL_MODAL_KEEPS_DATA,
        cta: COPY.MEMBERSHIP_CARD_UPGRADE_CTA,
        pro: false,
        upsell: true,
      };

    // INCOMPLETE lands here with NONE and anything unrecognised. That is
    // correct rather than a gap: Membership.js treats incomplete as neither
    // isPro nor isPastDue, so /membership shows those users the paywall — and
    // a card promising MANAGE would send them somewhere that offers instead.
    default:
      return {
        eyebrow: COPY.MEMBERSHIP_STATUS_FREE,
        standing: null,
        headline: COPY.MEMBERSHIP_CARD_FREE_HEADLINE,
        detail: COPY.MEMBERSHIP_CARD_FREE_DETAIL,
        cta: COPY.MEMBERSHIP_CARD_UPGRADE_CTA,
        pro: false,
        upsell: true,
      };
  }
}

export default function MembershipCard({ className = '' }) {
  const { entitlement, isLoading, error } = useEntitlement();

  // A card that defaults to FREE ACCOUNT while loading tells a paying member
  // they are not one, every single visit. The skeleton holds the same box so
  // the page below it does not jump when the snapshot lands.
  if (isLoading) {
    return (
      <div
        data-testid="membership-card-loading"
        aria-busy="true"
        className={`rounded-card border border-hairline-soft bg-[#161615] h-[196px] lg:h-[188px] animate-pulse ${className}`.trim()}
      />
    );
  }

  const {
    eyebrow, standing, headline, detail, cta, pro, upsell,
  } = resolveMembershipCard(entitlement, error);

  // Aurum for the sell, Bone for everything else. AurumButton's `outline`
  // variant is the same treatment MembershipCTA and ProOfferPanel use, so the
  // offer looks identical wherever it appears; `shadow-aurum-glow` makes the
  // glow a resting state rather than hover-only, which on a touch device is
  // the difference between a glow and no glow at all.
  const Cta = upsell ? AurumButton : BoneButton;

  return (
    <div
      data-testid="account-status"
      className={[
        'rounded-card border p-5 lg:p-6',
        pro
          ? 'border-[rgba(201,164,92,0.32)] bg-[#1B1B1A]'
          : 'border-hairline-soft bg-[#161615]',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 min-w-0">
          {pro ? (
            <LuSparkles size={14} className="text-aurum flex-shrink-0" aria-hidden="true" />
          ) : (
            <LuUser size={14} className="text-bone-dim flex-shrink-0" aria-hidden="true" />
          )}
          <span
            className={[
              'text-[11px] lg:text-xs tracking-[0.22em] truncate',
              pro ? 'text-aurum-bright' : 'text-bone-dim',
            ].join(' ')}
          >
            {eyebrow}
          </span>
        </span>

        {/* The standing marker's dot is AURUM, not green. DESIGN.md §2.2
            forbids Green and every other saturated hue outright, and §14 bans
            coloured status dots by name. The Aurum dot is the sanctioned form
            (§6.1, on TabBar and VectorPill). Gold is the product's only way of
            saying "live". */}
        {standing && (
          <span className="flex items-center gap-2 flex-shrink-0">
            <span
              aria-hidden="true"
              className={[
                'w-1.5 h-1.5 rounded-full',
                pro ? 'bg-aurum' : 'bg-bone-dim',
              ].join(' ')}
            />
            <span className="text-[10px] lg:text-[11px] tracking-[0.22em] text-bone-dim">
              {standing}
            </span>
          </span>
        )}
      </div>

      <div className="mt-4 lg:mt-5 text-[26px] lg:text-[30px] leading-tight text-bone">
        {headline}
      </div>
      {/* Absent for the paying states. The price and the renewal date are
          billing DETAIL, and detail now has a page — repeating it here made
          the card a worse version of the same table. What is left is the one
          fact and the door to the rest. */}
      {detail && (
        <div className="mt-1.5 text-[14px] lg:text-[15px] text-bone-dim">
          {detail}
        </div>
      )}

      <Cta
        to={ROUTES.MEMBERSHIP}
        fullWidth
        className={upsell ? 'mt-5 lg:mt-6 shadow-aurum-glow' : 'mt-5 lg:mt-6'}
        trailingIcon={<LuChevronRight size={16} aria-hidden="true" />}
        data-testid={CTA_TESTID}
      >
        {cta}
      </Cta>
    </div>
  );
}
